import React, { FC, useContext, useLayoutEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArchivalAidMetadata,
  ArchivalCollection,
  ArchivalUnitCopy,
  Result,
} from 'types';

import { Theme, createStyles, makeStyles, useTheme } from '@material-ui/core';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { levelIcon } from 'components/grid/grid-hooks/breadcrumbs-hook';
import { useQuery } from 'react-query';
import fetch from 'utils/fetch';

import { GridUrlParams } from '../types/types';
import AidListItem from './aid-list-item';
import { useArchivalAidDetail } from '../hooks/aid-detail-hook';
import { GridRootUrl, HierarchyContext } from '../context/hierarchy-context';
import { useArchivalAidsListByCollection } from 'components/grid/grid-api';
import useMeasure from 'react-use-measure';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    active: {
      backgroundColor: theme.palette.primary.dark,
      color: 'white!important',
      padding: '0px 4px',
    },
    link: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    icon: {
      marginRight: 4,
      fontSize: 11,
    },

    listItem: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.grey[300],
    },
    listItemActive: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.primary.light,
    },
    itemWrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    itemText: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      paddingRight: 12,
    },
  })
);

export const useAidChildren = (id?: string, enabled?: boolean) =>
  useQuery(
    ['aid-children', id],
    () =>
      fetch('/api/archival-unit/public/list', {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sort: [{ field: 'numericId', order: 'ASC' }],
          filters: [
            // TODO: test na invalidated?
            {
              field: 'describingArchivalAids.id',
              operation: 'EQ',
              value: id,
            },
          ],
          size: -1,
        }),
      }).then(async (r) => (await r.json()) as Result<ArchivalUnitCopy>),
    { enabled: !!id && enabled }
  );

const CollectionChild = ({ aid }: { aid: ArchivalAidMetadata }) => {
  const { archivalAidId, archivalCollectionId } = useContext(HierarchyContext);
  const isActive = archivalAidId === aid.id;
  const classes = useStyles();
  const theme = useTheme();
  //const Icon = levelIcon['fonds'];
  const AidIcon = levelIcon['aid'];

  //  const listItemClass = isActive ? classes.listItemActive : classes.listItem;

  const aidDetail = useArchivalAidDetail(isActive ? aid.id : undefined);

  const searchParams = new URLSearchParams(window.location.search);
  const originatorId = searchParams.get('originator') ?? undefined;

  const originatorParam = `${
    originatorId ? `?originator=${originatorId}` : ''
  }`;

  const createLink = (): string => {
    if (aid.hasEadNode) {
      //return `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}/pomucka/${aid.id}/ead/${aid.eadNodeId}`;
      return `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}/ead/${aid.eadNodeId}${originatorParam}`;
    }
    return `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}/pomucka/${aid.id}${originatorParam}`;
  };

  if (isActive && aidDetail.isSuccess) {
    return <AidListItem aid={aidDetail.data} level={0} />;
  }

  return (
    <Link key={aid.id} className={`${classes.link} `} to={createLink()}>
      <div
        className={classes.itemWrapper}
        style={{
          backgroundColor:
            archivalAidId === aid.id ? theme.palette.primary.dark : '',
        }}
      >
        <div className={classes.itemText}>
          <span>
            <AidIcon className={classes.icon} />
          </span>
          {aid.name}
        </div>
      </div>
    </Link>
  );
};

type Props = {
  collection: ArchivalCollection;
  itemIndex: number;
  setHeight: (height: number) => void;
  height: number;
  activeRowIndex: number;
};

const CollectionListItem: FC<Props> = ({
  collection,
  setHeight,
  height,
  activeRowIndex,
  itemIndex,
}) => {
  const { id } = useParams<GridUrlParams>();

  const ref = useRef<HTMLDivElement>(null);
  const isActive = collection.id === id;

  const { aids } = useArchivalAidsListByCollection(collection.id, isActive);

  const searchParams = new URLSearchParams(window.location.search);
  const originatorId = searchParams.get('originator') ?? undefined;

  const [wrapRef, { height: wHeight }] = useMeasure();

  const createLink = (archivalUnitId?: string): string => {
    const originatorParam = `${
      originatorId ? `?originator=${originatorId}` : ''
    }`;
    if (archivalUnitId) {
      return `${GridRootUrl.COLLECTIONS}/${collection.id}/pomucka/${archivalUnitId}${originatorParam}`;
    }

    return `${GridRootUrl.COLLECTIONS}/${collection.id}${originatorParam}`;
  };

  useLayoutEffect(() => {
    if (isActive) {
      const newHeight = ref.current?.getBoundingClientRect?.()?.height ?? 25;
      if (height !== newHeight || activeRowIndex !== itemIndex) {
        setHeight(newHeight + wHeight);
      }
    }
  }, [ref, isActive, height, setHeight, activeRowIndex, itemIndex, wHeight]);

  const classes = useStyles();
  //const theme = useTheme();
  const Icon = levelIcon['fonds'];
  //const AidIcon = levelIcon['aid'];

  const listItemClass =
    isActive && collection?.id === id
      ? classes.listItemActive
      : classes.listItem;

  return (
    <div ref={ref} style={{ paddingLeft: 10 }}>
      <Link className={`${classes.link} ${listItemClass}`} to={createLink()}>
        <div className={classes.itemWrapper}>
          <div className={classes.itemText}>
            <span>
              <Icon className={classes.icon} />
            </span>
            {collection?.name}
          </div>
          {collection?.hasChildren ? (
            isActive ? (
              <ExpandLessIcon classes={{ root: listItemClass }} />
            ) : (
              <ExpandMoreIcon classes={{ root: listItemClass }} />
            )
          ) : null}
        </div>
      </Link>

      {isActive && aids && (
        <div style={{ color: 'white', paddingLeft: 16 }} ref={wrapRef}>
          {(aids ?? []).map((d) => (
            <CollectionChild key={d.id} aid={d} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionListItem;
